import type { FC, ReactNode } from "react";

import { Badge } from "./Badge";
import { TEXT_CLASSES } from "./Text";

interface SectionHeaderProps {
  title: string;
  icon?: ReactNode;
  className?: string;
  /**
   * Visual variant:
   * - 'default': Larger bold heading (text-sm font-bold)
   * - 'label': Smaller uppercase label style (text-xs uppercase tracking-wide)
   * - 'kicker': Tiny uppercase kicker (text-xs uppercase tracking-wide)
   */
  variant?: "default" | "label" | "kicker";
  /**
   * Optional badge text to display after the title
   */
  badge?: string;
  /**
   * Optional action element to display on the right side of the header
   */
  action?: ReactNode;
}

const variantStyles = {
  default:
    "mb-2 font-mono text-[11px] font-bold uppercase tracking-widest text-gray-900 dark:text-white",
  kicker: `${TEXT_CLASSES.label} mb-1.5`,
  label: `${TEXT_CLASSES.label} mb-1.5`,
};

export const SectionHeader: FC<SectionHeaderProps> = ({
  title,
  icon,
  className = "",
  variant = "default",
  badge,
  action,
}) => (
  <div
    className={`flex items-center justify-between ${variantStyles[variant]} ${className}`}
  >
    <h4 className="flex items-center gap-2">
      {icon && (
        <div className={variant === "default" ? "text-gray-500" : ""}>
          {icon}
        </div>
      )}
      {title}
      {badge && <Badge size="sm">{badge}</Badge>}
    </h4>
    {action && <div className="flex items-center">{action}</div>}
  </div>
);
